import React from "react";
import { Background } from "../../../BuildingBlocks/Background";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { FPMarketingSectionTitle } from "../../Simple/FPMarketingSectionTitle/FPMarketingSectionTitle";
import { TextAlign } from "../../../BuildingBlocks/TextAlign";
import { FPMarketingParagraph } from "../../Simple/FPMarketingParagraph/FPMarketingParagraph";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { FrontColors } from "../../../../models/FrontColors";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { FrontSpacing } from "../../../../models/FrontSpacing";
import { Flex } from "../../../BuildingBlocks/Flex";
import { MaxWidth } from "../../../BuildingBlocks/MaxWidth";
import { FPMarketingHelplineCTA } from "../../Complex/FPMarketingHelplineCTA/FPMarketingHelplineCTA";
import { TProbateLeadForm } from "../../../../../../domain/codecs/form/ProbateLeadForm";
import { FPLandingTitle } from "../../Simple/FPLandingTitle/FPLandingTitle";
import { FontRobotoSlab } from "../../../BuildingBlocks/FontRobotoSlab";
import { Text } from "../../../BuildingBlocks/Text";

type TLandingPageDesktopHelpProps = {
    form: TProbateLeadForm;
    onChange: (form: TProbateLeadForm) => void;
    onSubmit: (form: TProbateLeadForm) => void,
};

export const FPLandingPageDesktopHelp = (props: React.PropsWithChildren<TLandingPageDesktopHelpProps>): JSX.Element => {

    return (
        <Background 
            padding="180px 40px 240px 40px"
            color={FrontColors.NEUTRAL_18}
            width="100%"
        >
            <SpacingRow justifyContent="center">
                <MaxWidth width="1200px">
                    <SpacingColumn spacing="120px">

                        <SpacingColumn spacing="100px">
                            {/* TITLE & HEADING */}
                            <SpacingColumn
                                spacing="40px"
                                justifyContent="center"
                            >
                                {/* HEADING */}
                                <FPLandingTitle 
                                    color={FrontColors.PROBATE_0}
                                    align="center"
                                    size={38}
                                >
                                    Let us help you get started.
                                </FPLandingTitle>

                                {/* SUB TITLE */}
                                <FPMarketingParagraph 
                                    color={FrontColors.NEUTRAL_INK}
                                    size={22}
                                    align="center"
                                >
                                    These are the <WeightBold>first three things</WeightBold> you should do to <WeightBold>get started</WeightBold> with probate.
                                </FPMarketingParagraph>
                            </SpacingColumn>

                            {/* 1 & 2 */}
                            <SpacingRow
                                spacing="80px"
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                {/* FIRST */}
                                <StartingPointParagraph number={1}>
                                    The first thing you want to do when going through probate,
                                    is to <WeightBold>list assets.</WeightBold> Examples of assets are: <WeightBold>house, items and savings.</WeightBold>
                                </StartingPointParagraph>

                                {/* SECOND */}
                                <StartingPointParagraph number={2}>
                                    The second thing you want to do is <WeightBold>list debts.</WeightBold>&nbsp;
                                    Examples of debts are: <WeightBold>loans, mortgages and overdrafts.</WeightBold>
                                </StartingPointParagraph>
                            </SpacingRow>
                            
                            {/* 3 & CTA */}
                            <SpacingRow
                                spacing="80px"
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <StartingPointParagraph number={3}>
                                    Once you have done this you can either <WeightBold>do the probate yourself</WeightBold> or
                                    hire an <WeightBold>expert.</WeightBold> Your <WeightBold>choice</WeightBold> will affect
                                    how <WeightBold>slow or fast</WeightBold> the process is.
                                </StartingPointParagraph>

                                <MaxWidth width="400px">
                                    <SpacingColumn spacing={"30px"}>
                                        <StartingPointParagraph>
                                            If you would like more help and simple advice have us call you.
                                        </StartingPointParagraph>

                                        <MaxWidth width="350px">
                                            <FPMarketingHelplineCTA
                                                form={props.form}
                                                name="landing-page-0__help-view"
                                                onChange={props.onChange}
                                                onSubmit={props.onSubmit}
                                            />
                                        </MaxWidth>
                                    </SpacingColumn>
                                </MaxWidth>
                            </SpacingRow>
                        </SpacingColumn>
                    </SpacingColumn>
                </MaxWidth>
            </SpacingRow>
        </Background>
    );
};

const StartingPointParagraph = (props: React.PropsWithChildren<{ number?: number }>) => {
    return (
        <MaxWidth width="400px">
            <SpacingRow
                alignItems="flex-start"
                spacing="20px"
            >
                {props.number !== undefined &&
                    <FontRobotoSlab>
                        <Text
                            width="100%"
                            size={40}
                            weight={500}
                            letterSpacing="1px"
                            lineHeight={1.4}
                            align={"left"}
                            color={FrontColors.PROBATE_0}
                        >
                            {props.number}.
                        </Text>
                    </FontRobotoSlab>
                }

                <FPMarketingParagraph 
                    color={FrontColors.NEUTRAL_INK}
                    size={22}
                >
                    {props.children}
                </FPMarketingParagraph>
            </SpacingRow>
        </MaxWidth>
    );
}
